import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ApiService } from 'src/app/services/api.service';
import { UtilService } from 'src/app/services/util.service';
import { NodataFoundComponent } from 'src/app/widget/nodata-found/nodata-found.component';
import Swal from 'sweetalert2';
import { NG_ICON_DIRECTIVES } from '@ng-icons/core';


@Component({
  selector: 'app-match-percentage-details',
  standalone: true,
  imports: [CommonModule, FormsModule, NodataFoundComponent,
    NG_ICON_DIRECTIVES,
  ],
  templateUrl: './match-percentage-details.component.html',
  styleUrls: ['./match-percentage-details.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-50%) translateY(-5px)' }),
        animate('400ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateX(-50%) translateY(0)' }))
      ]),
      transition(':leave', [
        animate('400ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateX(-50%) translateY(-5px)' }))
      ])
    ])
  ]
})
export class MatchPercentageDetailsComponent implements OnInit {

  @Input() isOpen: boolean = false;
  @Input() jobId: any;
  @Input() candidateId: any;
  @Input() pageFrom:any;
  @Input() organisationId:any;

  @Output() close = new EventEmitter<void>();

  constructor(private api:ApiService , private util: UtilService) {}

  userId:any;
  ngOnInit(): void {
    this.userId = localStorage.getItem('userId');
    this.getPercentageDetails();
  }  

  closeDetails(): void {
    this.close.emit();
  }

  getProgressColor(percentage: any): string {
    return percentage > 50 ? '#4CAF50' : '#FFCDD2';
  }

  getProgressTextColor(percentage: number): string {
    return percentage > 50 ? '#2E7D32' : '#C62828';
  }

  getScoreMatchLabel(score:any){
    if(score >= 80) return 'Good Match';
    if(score >= 60) return 'Average Match';
    return 'Bad Match';
  }

  /** Per-button "saving…" state so an inline spinner shows on the exact
   *  button being submitted (no full-page loader). Values:
   *  `savingOverall` holds the accuracy value in flight; `savingStrength`
   *  / `savingWeakness` hold 'AGREE' | 'DISAGREE'. */
  savingOverall: string | null = null;
  savingStrength: string | null = null;
  savingWeakness: string | null = null;

  // ── Feedback submission ────────────────────────────────────────────
  // ONE request carries all three sections (overall + strengths + weaknesses).
  // There is no Save button - each control writes on click - so a click updates
  // `detail` and then queues a single save that sends the full current state.
  // Sending only the clicked section is what made feedback randomly disappear:
  // the server reads the ranking from Elasticsearch, edits it and writes it
  // back, and Elasticsearch only makes a write searchable ~1s later, so the
  // second of three rapid calls read the pre-first-call copy and wrote it back,
  // dropping the earlier section. Every request now being complete makes that
  // impossible.

  /** Debounce window - coalesces rapid clicks into one request. */
  private static readonly FEEDBACK_DEBOUNCE_MS = 400;
  private fbTimer: any = null;
  private fbInFlight = false;
  private fbDirty = false;
  private fbDetail: any = null;

  selectAccuracy(s:any, detail:any){
    // Map UI value to API value
    let feedbackValue = '';
    switch(s){
      case 'accurate':
        feedbackValue = 'ACCURATE';
        break;
      case 'somewhat':
        feedbackValue = 'SOMEWHAT_ACCURATE';
        break;
      case 'not-accurate':
        feedbackValue = 'NOT_ACCURATE';
        break;
    }
    if(!feedbackValue) return;

    // Optimistic - the button highlights immediately, the save follows.
    this.savingOverall = feedbackValue;
    detail.overAllFeedback = feedbackValue;
    this.displayTooltip('accuracy-' + s);

    this.queueFeedbackSave(detail);
  }

  /** Mark feedback changed and schedule the single save. */
  private queueFeedbackSave(detail:any): void {
    this.fbDetail = detail;
    this.fbDirty = true;
    if (this.fbTimer) clearTimeout(this.fbTimer);
    this.fbTimer = setTimeout(() => this.flushFeedback(),
      MatchPercentageDetailsComponent.FEEDBACK_DEBOUNCE_MS);
  }

  /** Send the current state of all three sections as ONE request. Requests are
   *  strictly serialised - a click during a save re-fires afterwards - so an
   *  older payload can never land after a newer one. */
  private flushFeedback(): void {
    if (this.fbInFlight || !this.fbDirty) return;

    const detail = this.fbDetail;
    if (!detail) return;

    let payload = this.getTypeAndGroupType(this.pageFrom);
    if (!payload) return;
    if (!payload.userId) {
      payload.userId = this.userId;
    }

    // Only sections this modal actually knows are sent. The server leaves an
    // omitted section exactly as stored, so "not known here" can never blank a
    // value written from another screen.
    if (detail.overAllFeedback) payload.overAllFeedback = detail.overAllFeedback;
    if (detail.strengthsFeedback) {
      payload.strengthsFeedback = detail.strengthsFeedback;
      if (detail.strengthsComments) payload.strengthsComments = detail.strengthsComments;
    }
    if (detail.weaknessesFeedback) {
      payload.weaknessesFeedback = detail.weaknessesFeedback;
      if (detail.weaknessesComments) payload.weaknessesComments = detail.weaknessesComments;
    }

    if (!payload.overAllFeedback && !payload.strengthsFeedback && !payload.weaknessesFeedback) {
      this.fbDirty = false;
      return;
    }

    this.fbDirty = false;
    this.fbInFlight = true;

    this.api.updatePut('jobs/updateFeedbacks', payload).subscribe({
      next: (res) => {
        console.log('feedback saved', res);
        this.fbInFlight = false;
        this.clearSavingFlags();
        if (this.fbDirty) this.flushFeedback();
      },
      error: (err) => {
        console.log('error feedback', err);
        this.fbInFlight = false;
        this.clearSavingFlags();
      }
    });
  }

  private clearSavingFlags(): void {
    this.savingOverall = null;
    this.savingStrength = null;
    this.savingWeakness = null;
  }

  isLoading: boolean = false;
  private retryAttempt: number = 0;

  getTypeAndGroupType(pageName:any) : any{
    console.log('page From : ',pageName);
    // Ensure organisationId is retrieved from localStorage if not provided
    const orgId = this.organisationId || localStorage.getItem('organisationId');

    switch(pageName){
      case 'job-applicants':
      case 'candidate-applied':
        return {type : 'JOB', groupType: 'APPLICANT_CANDIDATE',
          sourceEntityId : this.jobId,targetEntityId : this.candidateId}

      case 'talent-pool':
        return {type : 'JOB', groupType: 'TALENT_POOL',
          sourceEntityId : this.jobId,targetEntityId : this.candidateId}

      case 'suggested-candidates':
        return {type : 'JOB', groupType: 'SUGGESTED_CANDIDATE',
          sourceEntityId : this.jobId,targetEntityId : this.candidateId}

      case 'candidates-invited':
        return {type : 'JOB', groupType: 'CANDIDATE_INVITED',
          sourceEntityId : this.jobId,targetEntityId : this.candidateId}

      case 'jobsApplied':
        return {type : 'CANDIDATE', groupType: 'APPLICANT_JOB',
          sourceEntityId : this.candidateId,targetEntityId : this.jobId}

      case 'suggestedJobs':
        return {type : 'CANDIDATE', groupType: 'SUGGESTED_JOB',
          sourceEntityId : this.candidateId,targetEntityId : this.jobId}

      case 'jobsInvited':
        return {type : 'CANDIDATE', groupType: 'JOB_INVITED',
          sourceEntityId : this.candidateId,targetEntityId : this.jobId}
    }
  }

  details:any;
  hasCompleteInsights: boolean = false; // Track if we have detailed insights
  hasQuantitativeCriteria: boolean = false; // Track if we have percentage-based criteria

  getPercentageDetails(){
    this.isLoading = true;
    // Don't use util.startLoader() - it blocks entire app during AI processing
    this.retryAttempt = 0;

    const payload = this.getTypeAndGroupType(this.pageFrom);
    console.log('🎯 Fetching ranking details with payload:', payload);
    console.log('📍 Page:', this.pageFrom, '| Job:', this.jobId, '| Candidate:', this.candidateId);

    // Use default retry settings: 3 retries with 25s delay = ~75s total
    this.api.fetchRankingDetailsWithRetry(payload).subscribe({
      next: (res) => {
        console.log('✅ Ranking details received:', res);
        if(res.code == '00000'){
          // Handle both old format (ranking) and new format (rankings array)
          let ranking = null;

          if (res.data.ranking) {
            // Old format: single ranking object
            console.log('📦 Old format detected: res.data.ranking');
            const oldRanking = res.data.ranking;
            // Normalize old format to match new format structure
            ranking = {
              ...oldRanking,
              // Reason: prioritize candidateReason > jobReason > reason
              reason: oldRanking.candidateReason || oldRanking.jobReason || oldRanking.reason,
              // Strengths: combine pros, jobStrengths, and strengths
              strengths: [
                ...(oldRanking.pros || []),
                ...(oldRanking.jobStrengths || []),
                ...(oldRanking.strengths || [])
              ],
              // Weaknesses: combine cons, jobWeaknesses, and weaknesses
              weaknesses: [
                ...(oldRanking.cons || []),
                ...(oldRanking.jobWeaknesses || []),
                ...(oldRanking.weaknesses || [])
              ]
            };
          } else if (res.data.rankings && res.data.rankings.length > 0) {
            // New format: rankings array
            console.log('📦 New format detected: res.data.rankings (array)');
            const firstRanking = res.data.rankings[0];
            if (firstRanking.targetEntities && firstRanking.targetEntities.length > 0) {
              const targetEntity = firstRanking.targetEntities[0];
              // Merge ranking data with targetEntity data
              // Support both old format (reason, strengths, weaknesses) and new format (candidateReason/jobReason, pros/cons)
              ranking = {
                ...firstRanking,
                targetEntityId: targetEntity.targetEntityId,
                score: targetEntity.score,
                // Reason: prioritize candidateReason > jobReason > reason
                reason: targetEntity.candidateReason || targetEntity.jobReason || targetEntity.reason || firstRanking.reason,
                // Strengths: combine pros, jobStrengths, and strengths
                strengths: [
                  ...(targetEntity.pros || []),
                  ...(targetEntity.jobStrengths || []),
                  ...(targetEntity.strengths || firstRanking.strengths || [])
                ],
                // Weaknesses: combine cons, jobWeaknesses, and weaknesses
                weaknesses: [
                  ...(targetEntity.cons || []),
                  ...(targetEntity.jobWeaknesses || []),
                  ...(targetEntity.weaknesses || firstRanking.weaknesses || [])
                ],
                updatedOn: targetEntity.updatedOn || firstRanking.updatedOn,
                // Feedback lives on the target entity in the new shape. These were commented out,
                // so the modal never saw stored feedback and fell back to the 'TEMPORARY' placeholder
                // below - which is also why the panel now has to know its own state before it can
                // send all three sections in one request.
                strengthsFeedback: targetEntity?.strengthsFeedback ?? firstRanking?.strengthsFeedback,
                strengthsComments: targetEntity?.strengthsComments ?? firstRanking?.strengthsComments,
                weaknessesFeedback: targetEntity?.weaknessesFeedback ?? firstRanking?.weaknessesFeedback,
                weaknessesComments: targetEntity?.weaknessesComments ?? firstRanking?.weaknessesComments,
                overAllFeedback: targetEntity?.overAllFeedback ?? firstRanking?.overAllFeedback,
              };
              console.log('✅ Merged ranking from rankings array:', ranking);
            }
          }

          if(ranking){
            // Build details array from the ranking object
            const detailItem = {
              targetEntityId: ranking.targetEntityId,
              score: ranking.score,
              reason: ranking.reason,
              strengths: ranking.strengths || [],
              weaknesses: ranking.weaknesses || [],
              updatedOn: ranking.updatedOn,
              // No placeholder: these are sent back to the server on the next click, so an
              // invented value would be written into the ranking table as real feedback.
              strengthsFeedback: ranking?.strengthsFeedback ?? null,
              strengthsComments: ranking?.strengthsComments ?? null,
              weaknessesFeedback: ranking?.weaknessesFeedback ?? null,
              weaknessesComments: ranking?.weaknessesComments ?? null,
              overAllFeedback: ranking?.overAllFeedback ?? null,
              criteria: []
            };

            // Check if we have complete insights (strengths/weaknesses data)
            const hasStrengths = detailItem.strengths && detailItem.strengths.length > 0;
            const hasWeaknesses = detailItem.weaknesses && detailItem.weaknesses.length > 0;
            this.hasCompleteInsights = hasStrengths || hasWeaknesses;

            // Extract criteria percentages from strengths and weaknesses
            let combinedList = [...detailItem.strengths, ...detailItem.weaknesses];

            console.log('🔍 Combined list for criteria extraction:', combinedList);

            const allCriteria = [
              { label: 'Title', percentage: this.getCriteria(combinedList, 'Title') },
              { label: 'Skills', percentage: this.getCriteria(combinedList, 'Skills') },
              // { label: 'Secondary Skills', percentage: this.getCriteria(combinedList, 'Secondary Skills') },
              { label: 'Experience', percentage: this.getCriteria(combinedList, 'Experience') },
              { label: 'Location', percentage: this.getCriteria(combinedList, 'Location') },
              // { label: 'Education', percentage: this.getCriteria(combinedList, 'Education') },
              { label: 'Work Mode', percentage: this.getCriteria(combinedList, 'Work Mode') },
              // { label: 'Engagement Type', percentage: this.getCriteria(combinedList, 'Engagement Type') },
            ];

            console.log('📊 All criteria before filter:', allCriteria);

            // Show ALL criteria, including 0% (don't filter them out)
            detailItem.criteria = allCriteria;

            // Check if we have quantitative criteria (any percentage found, even 0%)
            this.hasQuantitativeCriteria = allCriteria.some(c =>
              combinedList.some(item =>
                typeof item === 'string' && item.includes('(') && item.includes('%)')
              )
            );

            console.log('✨ hasQuantitativeCriteria:', this.hasQuantitativeCriteria);

            console.log('📊 Data completeness check:', {
              hasStrengths,
              hasWeaknesses,
              hasCompleteInsights: this.hasCompleteInsights,
              hasQuantitativeCriteria: this.hasQuantitativeCriteria,
              strengthsCount: detailItem.strengths.length,
              weaknessesCount: detailItem.weaknesses.length,
              criteriaPercentages: detailItem.criteria.map(c => `${c.label}: ${c.percentage}%`)
            });

            // Clean up strengths and weaknesses text
            if(detailItem.strengths && detailItem.strengths.length > 0){
              console.log('🔧 BEFORE cleanup - strengths:', detailItem.strengths);
              detailItem.strengths = detailItem.strengths.map(e => this.getOnlyText(e));
              console.log('✅ AFTER cleanup - strengths:', detailItem.strengths);
            }

            if(detailItem.weaknesses && detailItem.weaknesses.length > 0){
              console.log('🔧 BEFORE cleanup - weaknesses:', detailItem.weaknesses);
              detailItem.weaknesses = detailItem.weaknesses.map(el => this.getOnlyText(el));
              console.log('✅ AFTER cleanup - weaknesses:', detailItem.weaknesses);
            }

            this.details = [detailItem];
            console.log('📋 Final details object:', this.details);
            console.log('📋 Final details[0].strengths:', this.details[0]?.strengths);
            console.log('📋 Final details[0].weaknesses:', this.details[0]?.weaknesses);
          }else {
            console.warn('⚠️ No ranking data found in response');
            Swal.fire('No ranking details available','','info');
          }
        }else {
          Swal.fire('Something went wrong','','error');
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;

        console.error('Match percentage details error:', err);

        // Handle processing timeout (after max retries exhausted)
        if (err.timeout && err.isProcessing) {
          Swal.fire({
            title: 'Analysis In Progress',
            text: err.message || 'The AI analysis is taking longer than expected. Please try again in a moment.',
            icon: 'warning',
            confirmButtonText: 'OK'
          });
        } else {
          // Other errors
          Swal.fire({
            title: 'Error',
            text: 'Failed to fetch match insights. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      }
    });
  }

  getCriteria(list: any[], type: string): number {
    for (const item of list) {
      if (!item || typeof item !== 'string') continue;

      // Enhanced format patterns (new)
      if (type === 'Skills' && (item.includes('Skills Match') || item.includes('Primary Skills:'))) {
        return this.extractPercentage(item);
      }
      if (type === 'Primary Skills' && (item.includes('Primary Skills Match') || item.includes('Primary Skills:'))) {
        return this.extractPercentage(item);
      }
      if (type === 'Secondary Skills' && (item.includes('Secondary Skills Match') || item.includes('Secondary Skills:'))) {
        return this.extractPercentage(item);
      }
      if (type === 'Experience' && (item.includes('Experience Level') || item.includes('Experience Match'))) {
        return this.extractPercentage(item);
      }
      if (type === 'Title' && (item.includes('Job Title Alignment') || item.includes('Title Match'))) {
        return this.extractPercentage(item);
      }
      if (type === 'Education' && (item.includes('Education Match') || item.includes('Education Level'))) {
        return this.extractPercentage(item);
      }
      if (type === 'Location' && (item.includes('Location Compatibility') || item.includes('Location Match'))) {
        return this.extractPercentage(item);
      }
      if (type === 'Work Mode' && (item.includes('Work Mode Match') || item.includes('Work Mode Compatibility'))) {
        return this.extractPercentage(item);
      }
      if (type === 'Engagement Type' && (item.includes('Engagement Type') || item.includes('Engagement Match'))) {
        return this.extractPercentage(item);
      }
    }
    return 0;
  }

  extractPercentage(text: string): number {
    const match = text.match(/\((\d+)%\)/);
    return match ? Number(match[1]) : 0 ;
  }

  getOnlyText(res){
    if(res){
      const detail = res.split(':');
      // If there's a colon, join everything after the first part (in case of multiple colons)
      if(detail.length > 1){
        // Remove the first part (label) and join the rest back together
        detail.shift(); // Remove first element
        return detail.join(':').trim(); // Join remaining parts and trim whitespace
      }
      return res;
    }

    return '';
  }

  // Tooltip state management
  showTooltip: { [key: string]: boolean } = {};
  private tooltipTimeouts: { [key: string]: any } = {};

  // Disagree input state management
  showDisagreeInput: { [key: string]: boolean } = {};
  disagreeReason: { [key: string]: string } = {};

  // The panels sit behind *ngIf, so these resolve only once the panel is rendered.
  @ViewChild('strengthDisagreeInput') strengthDisagreeInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('weaknessDisagreeInput') weaknessDisagreeInput?: ElementRef<HTMLTextAreaElement>;

  /**
   * Shows tooltip for a specific feedback type and hides it after 2 seconds
   * @param type - The feedback type identifier (e.g., 'strength' or 'weakness')
   */
  displayTooltip(type: string): void {
    // Clear any existing timeout for this type
    if (this.tooltipTimeouts[type]) {
      clearTimeout(this.tooltipTimeouts[type]);
    }

    // Show tooltip
    this.showTooltip[type] = true;

    // Hide tooltip after 2 seconds with fade out
    this.tooltipTimeouts[type] = setTimeout(() => {
      this.showTooltip[type] = false;
    }, 2000);
  }

  updateFeedback(from:any,feedBack:any,detail:any,comments?:any){
    this.applyCategoryFeedback(from, feedBack, detail, comments);
    if(feedBack === 'AGREE'){
      this.displayTooltip(from == 'STRENGTH' ? 'strength-agree' : 'weakness-agree');
    }
  }

  /** Apply a strengths/weaknesses rating to `detail` and queue the single save.
   *  A comment belongs to DISAGREE only; switching to AGREE drops it, matching
   *  what the server stores. */
  private applyCategoryFeedback(from:any, feedBack:any, detail:any, comments?:any): void {
    if(from == 'STRENGTH'){
      this.savingStrength = feedBack;
      detail.strengthsFeedback = feedBack;
      detail.strengthsComments = feedBack === 'DISAGREE' ? (comments ?? detail.strengthsComments) : null;
    } else {
      this.savingWeakness = feedBack;
      detail.weaknessesFeedback = feedBack;
      detail.weaknessesComments = feedBack === 'DISAGREE' ? (comments ?? detail.weaknessesComments) : null;
    }
    this.queueFeedbackSave(detail);
  }

  /**
   * Toggle the disagree input tooltip
   * @param type - 'strength' or 'weakness'
   * @param detail - detail object (for future use if needed)
   */
  toggleDisagreeInput(type: string, detail: any): void {
    // Toggle the input visibility
    this.showDisagreeInput[type] = !this.showDisagreeInput[type];

    // Clear the reason when opening
    if (this.showDisagreeInput[type]) {
      this.disagreeReason[type] = '';
      // Put the caret in the box so the user can type straight away. Deferred because
      // the textarea is behind *ngIf and only exists after this change-detection pass.
      setTimeout(() => {
        const input = type === 'strength' ? this.strengthDisagreeInput : this.weaknessDisagreeInput;
        input?.nativeElement.focus();
      });
    }
  }

  /**
   * Close the disagree input tooltip
   * @param type - 'strength' or 'weakness'
   */
  closeDisagreeInput(type: string): void {
    this.showDisagreeInput[type] = false;
    this.disagreeReason[type] = '';
  }

  /**
   * Submit the disagree feedback with reason
   * @param from - 'STRENGTH' or 'WEAKNESS'
   * @param feedBack - 'DISAGREE'
   * @param detail - detail object
   * @param type - 'strength' or 'weakness' (for UI state management)
   */
  submitDisagree(from: any, feedBack: any, detail: any, type: string): void {
    const reason = this.disagreeReason[type];

    if (!reason || reason.trim() === '') {
      return;
    }

    // Close the input tooltip
    this.showDisagreeInput[type] = false;

    // Submit the feedback
    this.updateFeedbackWithTooltip(from, feedBack, detail, reason);

    // Clear the reason
    this.disagreeReason[type] = '';
  }

  /**
   * Update feedback and show tooltip for disagree action
   */
  updateFeedbackWithTooltip(from: any, feedBack: any, detail: any, comments?: any) {
    this.applyCategoryFeedback(from, feedBack, detail, comments);
    if(feedBack === 'DISAGREE'){
      this.displayTooltip(from == 'STRENGTH' ? 'strength-disagree' : 'weakness-disagree');
    }
  }

}
